import { useCallback, useEffect, useState } from 'react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface CollapsiblePanelProps {
  /** Stable key so the collapsed state survives reloads and route changes. */
  id: string
  title: string
  /** How many filters are still in effect — shown while collapsed, because a
   *  hidden-but-active filter is otherwise invisible and very confusing. */
  activeCount?: number
  width?: string
  children: React.ReactNode
}

export function CollapsiblePanel({
  id,
  title,
  activeCount = 0,
  width = 'w-56',
  children,
}: CollapsiblePanelProps) {
  const { t } = useTranslation()
  const storageKey = `kva-panel-${id}`
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKey) === '1',
  )

  const toggle = useCallback(() => {
    setCollapsed((v) => {
      localStorage.setItem(storageKey, v ? '0' : '1')
      return !v
    })
  }, [storageKey])

  // Alt+S toggles, so the keyboard-driven flow does not need the mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  if (collapsed) {
    return (
      <aside className="flex w-9 shrink-0 flex-col items-center gap-2 border-r border-border py-2">
        <Tooltip
          label={t('panel.openWithShortcut', { title: title.toLowerCase() })}
          side="right"
        >
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggle}
            aria-label={t('panel.open', { title })}
          >
            <PanelLeftOpen />
          </Button>
        </Tooltip>
        {activeCount > 0 && (
          <span
            title={t('panel.activeFilters', { count: activeCount })}
            className="flex size-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground"
          >
            {activeCount}
          </span>
        )}
        {/* Vertical label keeps the strip identifiable without hover. */}
        <span
          className="mt-1 select-none text-[10px] uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: 'vertical-rl' }}
        >
          {title}
        </span>
      </aside>
    )
  }

  return (
    <aside className={cn('flex shrink-0 flex-col border-r border-border', width)}>
      <div className="flex shrink-0 items-center justify-between border-b border-border px-2 py-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <Tooltip label={t('panel.hideWithShortcut')} side="right">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggle}
            aria-label={t('panel.hide', { title })}
          >
            <PanelLeftClose />
          </Button>
        </Tooltip>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </aside>
  )
}
